'use client';

import { ElementRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';

/**
 * The app's checkbox — Radix's primitive, dressed.
 *
 * ## What changed, and why most of it was wrong rather than merely unfashionable
 *
 * Reported as "the current one doesn't look modern". Rendering it and reading its
 * classes turned up six things, and only one of them is taste:
 *
 * · **No border radius at all.** It drew a hard square, which is the loudest single
 *   tell — every current checkbox has a small radius. `rounded-[4px]` rather than a
 *   `rounded-*` token because the scale's neighbours are both wrong at this size:
 *   `rounded-sm` is 2px (still reads square at 17px) and `rounded-md` is 6px (reads
 *   like a squircle). 4px is the value shadcn itself ships for this control.
 * · **`border-primary` at rest.** A full-strength brand border on an *unchecked* box
 *   means a page of empty checkboxes shouts in brand colour. The convention — and
 *   what makes a form read as calm — is a neutral border at rest with brand fill only
 *   once checked. `border-input-strong` is that neutral rest border (see below).
 * · **The tick had no inset.** `<Check className="h-full w-full" />` drew the glyph
 *   edge to edge, so the box looked stuffed rather than ticked; an isolated visual
 *   review of a drawer using it reported "the tick nearly fills the box". The inset is
 *   `p-0.5` on the **root**, so the glyph stays `h-full w-full` and therefore keeps
 *   scaling with whatever size a caller overrides.
 * · **13.6px.** `tokens.icon()` is `h-4 w-4`, and this theme's density knob makes
 *   `--spacing` 3.4px, so four steps is 13.6px — not the 16px that token's own comment
 *   claims. One step up the *same* scale (`md` → `h-5 w-5` → 17px) rather than an
 *   invented pixel value, so it keeps riding the knob.
 * · **No transition**, so every toggle was an instant colour flip.
 * · **The rest border was invisible.** On `--input` (`oklch(0.9219)`) the edge sat
 *   near enough to `--background` that an unchecked box had to be hunted for —
 *   reported on the document upload dialog, where the certified-copy and
 *   analyse options both read as static labels rather than as choices. A checkbox
 *   is the one control that is *only* its border at rest, so `--input` — tuned for
 *   fields that also carry a fill, a label and text — is too faint for it.
 *
 *   Fixed **at a token**, never here. `border-input-strong` is one step darker
 *   than `border-input` and exists for exactly this case. Two wrong shapes were
 *   tried first and both are worth not repeating: a control-local
 *   `border-muted-foreground/40` (a restated literal — it silently became the
 *   *lighter* of the two once the token moved, inverting the bug it was added to
 *   fix), and darkening `--input` itself (VALE-404 — one control's legibility
 *   bought with fresh ink on all ~33 files that bind to it, plus two
 *   matter-overview cards that mis-bind to it going darker than their siblings).
 *
 *   (Thickening to 1.5px was tried before any of that and rendered
 *   indistinguishable: at `0.9219` no amount of ink makes the edge register.
 *   It is colour, not width.)
 * · **`indeterminate` rendered a tick — a real bug, not styling.** Radix renders its
 *   `Indicator` for `checked` *and* `indeterminate`, and that indicator only ever
 *   contained `<Check/>`. So a partially-selected "select all" was indistinguishable
 *   from a fully-selected one. Two surfaces pass `'indeterminate'` today
 *   (`download-forms-modal`, the LPR-notify story) and both showed the wrong glyph.
 *   It is a dash now.
 *
 * The two glyphs are swapped in **CSS** rather than by reading `props.checked`,
 * because the state can be uncontrolled: Radix stamps `data-state` on the indicator,
 * so `group-data-[state=…]` is right whoever owns the value.
 *
 * The API is unchanged — each of the 115 call sites is a drop-in, and `className`
 * still overrides, which is how the few callers wanting a different size keep working.
 */
const Checkbox = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  ref?: React.RefObject<ElementRef<typeof CheckboxPrimitive.Root> | null>;
}) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer border-input-strong bg-background inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[4px] border p-0.5 transition-colors',
      'hover:border-primary/60',
      'focus-visible:ring-info/70 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-hidden',
      'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
      'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
      'disabled:hover:border-input-strong disabled:cursor-not-allowed disabled:opacity-50',
      tokens.icon('md'),
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="group flex h-full w-full items-center justify-center text-current">
      <Check
        className="h-full w-full group-data-[state=indeterminate]:hidden"
        strokeWidth={3}
      />
      <Minus
        className="hidden h-full w-full group-data-[state=indeterminate]:block"
        strokeWidth={3}
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName || 'Checkbox';

export { Checkbox };
